import {Divider, Grid, Typography} from '@logistics-frontend/blocks'
import {useCurrencySignByCountryCode} from '@logistics-frontend/client-core/config'
import {useI18n} from '@logistics-frontend/core/i18n'
import {get} from 'lodash'
import React, {useMemo} from 'react'
import {useLocation, useParams} from 'react-router-dom'

import {Category, TariffsPageParams, TariffZonesItem} from '../../types'
import {filterTariffsByFields, getCategoryTypes, getDayTypes, sortArrayBy} from '../../utils'
import {Disclaimer} from '../disclaimer'
import {Filter} from '../filter'
import {PickupDetails} from '../pickup-details'
import {Requirements, SpecialRequirements} from '../requirenments'
import {RideDetails} from '../ride-details'
import {Transfers} from '../transfers'

type Props = {
  isPostPaymentEnabled?: boolean
  surgeLink?: string
  disclaimerKey?: string
  tariffZones: TariffZonesItem[]
  tariffList: Category[]
}

const TariffDetailsColumn: React.FC<Props> = ({
  isPostPaymentEnabled,
  surgeLink,
  disclaimerKey,
  tariffZones,
  tariffList,
}) => {
  const t = useI18n()
  const {country, category, zone} = useParams<TariffsPageParams>()

  const query = new URLSearchParams(useLocation().search)
  const optionFromQuery = query.get('option')
  const dayTypeFromQuery = query.get('day_type')
  const categoryTypeFromQuery = query.get('category_type')
  const transferFromQuery = query.get('transfer')

  const city = useMemo(() => {
    return tariffZones.find(z => z.name === zone)?.translation
  }, [zone, tariffZones])

  const tariffs = useMemo(() => {
    return tariffList.filter(t => t.category_name === category)
  }, [category, tariffList])

  const currencySign = useCurrencySignByCountryCode(country)

  const {tariffMatch, filterProps} = useMemo(() => {
    const tariffOptions = sortArrayBy<Category>('name_translate')(tariffs).map(({name, name_translate}) => ({
      value: name,
      label: name_translate,
    }))
    const currentTariffOption = optionFromQuery || get(tariffOptions, [0, 'value'], optionFromQuery)

    const filterObj: Partial<Category> = {
      category_name: category,
      name: currentTariffOption,
    }

    const categoryTypeOptions = sortArrayBy<Category>('category_type')(filterTariffsByFields(tariffs, filterObj)).map(
      ({category_type: value}) => ({
        value,
        label: getCategoryTypes(t)[value],
      }),
    )
    const currentCategoryType = categoryTypeFromQuery || get(categoryTypeOptions, [0, 'value'], categoryTypeFromQuery)
    filterObj.category_type = currentCategoryType as Category['category_type']

    const dayTypeOptions = filterTariffsByFields(tariffs, filterObj).map(({day_type: value}) => ({
      value,
      label: getDayTypes(t)[value],
    }))
    const currentDayType = Number(dayTypeFromQuery) || get(dayTypeOptions, [0, 'value'], Number(dayTypeFromQuery))
    filterObj.day_type = currentDayType as Category['day_type']

    const tariffMatches = filterTariffsByFields(tariffs, filterObj)
    const tariffMatch = tariffMatches.length === 1 ? tariffMatches[0] : null

    const filterProps = {
      option: {options: tariffOptions, value: currentTariffOption},
      categoryType: {options: categoryTypeOptions, value: currentCategoryType},
      dayType: {options: dayTypeOptions, value: String(currentDayType)},
    }

    return {tariffMatch, filterProps}
  }, [tariffs, optionFromQuery, category, categoryTypeFromQuery, dayTypeFromQuery, t])

  const {transferDetails, transferSelectProps} = useMemo(() => {
    const allTransfers = tariffMatch?.zonal_prices || []
    const transferOptions = allTransfers.map(({source_translate, destination_translate}, index) => ({
      value: index.toString(),
      label: `${source_translate} — ${destination_translate}`,
    }))
    const currentTransfer = transferFromQuery || get(transferOptions, [0, 'value'], transferFromQuery)
    const transferDetails = allTransfers[Number(currentTransfer)]

    return {transferSelectProps: {options: transferOptions, value: currentTransfer}, transferDetails}
  }, [tariffMatch, transferFromQuery])

  const taximeterZones = useMemo(
    () =>
      (tariffMatch?.special_taximeters || []).map(({zone_name, zone_name_translate}) => ({
        zone_name,
        zone_name_translate,
      })),
    [tariffMatch],
  )

  return (
    <>
      <Typography
        Component="h3"
        pb="m"
        pt="m"
        style={{
          paddingInline: 12,
        }}
        variant="body1"
      >
        {t('tariffs.column_title.tariff_details', {
          placeholder: {city},
        })}
      </Typography>
      <Divider />
      <Filter {...filterProps} />
      {tariffMatch ? (
        <>
          <Grid data-e2e="detail-tariff" spacing={2} container>
            <PickupDetails currencySign={currencySign} tariff={tariffMatch} zone={zone} />
            {taximeterZones.map(({zone_name, zone_name_translate}) => (
              <RideDetails
                key={zone_name}
                currencySign={currencySign}
                tariff={tariffMatch}
                taximeterZone={zone_name}
                taximeterZoneTranslate={zone_name_translate}
                zone={zone}
              />
            ))}
            <Requirements currencySign={currencySign} tariff={tariffMatch} />
            <SpecialRequirements isPostPaymentEnabled={isPostPaymentEnabled} tariff={tariffMatch} />
          </Grid>

          <Transfers
            currencySign={currencySign}
            transferDetails={transferDetails}
            transferSelectProps={transferSelectProps}
          />

          <Disclaimer
            city={city}
            country={country}
            disclaimerKey={disclaimerKey}
            surgeLink={surgeLink}
            tariff={tariffMatch}
            zone={zone}
          />
        </>
      ) : (
        <Typography pt="l" variant="body2">
          {t('tariffs.message.specify_tariff')}
        </Typography>
      )}
    </>
  )
}

export default TariffDetailsColumn
